import { classNames } from '@zambezi/sdk/common'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import DropdownList from './DropdownList'
import DropdownListTrigger from './DropdownListTrigger'

/**
 * @typedef {Object} TagLookupRemoveProps
 * @property {string[]} tags
 * @property {number} activeTag
 * @property {string} tagsId
 * @property {boolean} disabled
 * @property {number} offset
 * @property {() => void} onHide
 * @property {(tag:string) => (null | (tag:string, event: React.SyntheticEvent<HTMLElement, Event>) => void)} getTagRemoveHandler
 */

/**
 * @type {React.ForwardRefRenderFunction<HTMLElement, TagLookupRemoveProps>}
 * @param {TagLookupRemoveProps} param0
 * @param {import('react').Ref<HTMLElement>} ref
 */
function tagLookupRemove (
  { activeTag, disabled, getTagRemoveHandler, onHide, tags, tagsId, offset },
  ref
) {
  const [focused, setFocused] = useState(false)
  const dropdownListRef = useRef()
  const handleItemSelected = useCallback(
    (
      /** @type {string} */ tag,
      /** @type {number} */ index,
      /** @type {React.SyntheticEvent<Element, Event>} */ event
    ) => {
      const handler = getTagRemoveHandler(tag)
      handler && handler(tag, event)
    },
    [getTagRemoveHandler]
  )
  const handleKeyDown = useCallback(
    (/** @type {React.KeyboardEvent} */ event) => {
      event.stopPropagation()
      if (event.key === 'Escape') {
        onHide()
      }
    },
    [onHide]
  )
  const disabledIndices = useMemo(() => {
    const indices = []
    for (let i = 0; i < tags.length; i++) {
      if (!getTagRemoveHandler(tags[i])) indices.push(i)
    }
    return indices
  }, [tags, getTagRemoveHandler])

  useEffect(() => {
    setFocused(activeTag >= offset)
  }, [activeTag, offset])
  useEffect(() => {
    const { current } = dropdownListRef
    if (current) {
      if (focused) {
        current.show()
      } else {
        current.hide()
      }
    }
  }, [focused])

  return (
    <span
      className={classNames('zb-lookup', { 'zb-lookup-is-open': focused })}
      role='presentation'
      onKeyDown={handleKeyDown}
      ref={ref}
    >
      {tags.map((tag, index) => (
        <span
          role='option'
          aria-selected='true'
          className='zb-button-group-sr-only'
          id={`${tagsId}-${index + offset}`}
          key={tag}
        >
          {tag}
        </span>
      ))}
      <DropdownListTrigger
        isDisabled={disabled}
        dropdownList={
          <DropdownList
            data={tags}
            disabledItemsIndices={disabledIndices}
            header={<>Remove</>}
            onItemClick={handleItemSelected}
          />
        }
        className='zb-tag-lookup-remove-dropdown'
        tabIndex={-1}
        onHide={onHide}
        ref={dropdownListRef}
      >
        <span
          className={classNames('zb-tag', { 'zb-tag-is-focused': focused })}
        >
          +{tags.length}
        </span>
      </DropdownListTrigger>
    </span>
  )
}

export default forwardRef(tagLookupRemove)
